import {
  ChevronDown,
  Info,
  LogOut,
  User,
  UserPlus,
  Users,
  File,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLogin } from "@/hooks/use-login";
import { useRoom } from "@/hooks/use-room";
import { useZeroStatus } from "@/hooks/use-zero-status";

export default function TopBar() {
  const [isRoomOpen, setIsRoomOpen] = useState(false);
  const [isUserOpen, setIsUserOpen] = useState(false);
  const [isDocumentOpen, setIsDocumentOpen] = useState(false);
  const {
    currentRoom,
    currentBlockTree,
    setCurrentDocument,
    joinRoom,
    leaveRoom,
    showRoomInfo,
  } = useRoom();
  const { user, isAuthenticated, logout } = useLogin();
  const { isOnline } = useZeroStatus();
  const navigate = useNavigate();

  return (
    <div className="absolute top-0 left-0 right-0 z-20 bg-card/80 backdrop-blur-sm border-muted m-3 rounded-xl">
      <div className="flex items-center justify-between px-4 py-2">
        <div className="flex items-center gap-3">
          <Link to="/">
            <h1 className="text-lg font-semibold">Atlas</h1>
          </Link>

          {/* Zero Status Indicator */}
          <div className="flex items-center gap-1.5">
            {isOnline ? (
              <Wifi className="h-4 w-4 text-green-600" />
            ) : (
              <WifiOff className="h-4 w-4 text-red-500" />
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Document Dropdown */}
          <DropdownMenu open={isDocumentOpen} onOpenChange={setIsDocumentOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="flex items-center gap-2 bg-background/50"
              >
                <File className="h-4 w-4" />
                <span className="hidden md:inline">
                  {currentBlockTree
                    ? currentBlockTree.document.name
                    : "No active document"}
                </span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Documents</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/documents")}>
                <File className="h-4 w-4 mr-2" />
                Open document
              </DropdownMenuItem>

              {currentBlockTree && (
                <>
                  <DropdownMenuSeparator />

                  <DropdownMenuItem
                    onClick={() => {
                      setCurrentDocument(undefined);
                      navigate("/documents");
                    }}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Close Document
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Room Dropdown */}
          <DropdownMenu open={isRoomOpen} onOpenChange={setIsRoomOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="flex items-center gap-2 bg-background/50"
              >
                <Users className="h-4 w-4" />
                <span className="hidden md:inline">
                  {currentRoom
                    ? `Room: ${currentRoom.slice(0, 8)}...`
                    : "No room joined"}
                </span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Room Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={joinRoom}>
                <Users className="h-4 w-4 mr-2" />
                Join Room
              </DropdownMenuItem>

              {currentRoom && (
                <>
                  <DropdownMenuItem onClick={showRoomInfo}>
                    <Info className="h-4 w-4 mr-2" />
                    Room Info
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  <DropdownMenuItem onClick={leaveRoom}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Leave Room
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Dropdown */}
          <DropdownMenu open={isUserOpen} onOpenChange={setIsUserOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="flex items-center gap-2 bg-background/50"
              >
                <User className="h-4 w-4" />
                <span className="hidden md:inline">
                  {isAuthenticated
                    ? user?.name || user?.email || "User"
                    : "Guest"}
                </span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Account</DropdownMenuLabel>
              <DropdownMenuSeparator />

              {isAuthenticated ? (
                <DropdownMenuItem onClick={logout}>
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuItem onClick={() => navigate("/login")}>
                    <User className="h-4 w-4 mr-2" />
                    Login
                  </DropdownMenuItem>

                  <DropdownMenuItem onClick={() => navigate("/register")}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Register
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
